/**
 * Default Governance Policy — Migrates all scattered constants into one object.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.2
 *
 * Sources migrated:
 * - utils.ts: TIER_MIN_EXPLANATION_LENGTH, TIER_MIN_DISTRACTOR_LENGTH
 * - visualRequiredRules.ts: VISUAL_REQUIRED_FAMILIES
 * - familySupportRegistry.ts: requiresVisual/requiresHints flags
 * - governanceValidator.ts: inline thresholds (forbiddenB6Vocabulary, text length limits)
 * - questionTypeDefinitions.ts: type-specific required fields
 * - generateGovernanceMapFromSeeds(): per-family visual rules (auto-generated from seeds, s107)
 */

import type { GovernancePolicy } from './policyTypes';
import type { GateThresholds } from './issueContract';
import { DEFAULT_GATE_THRESHOLDS } from './issueContract';
import { buildVisualPolicyFromMap } from './visualPolicyBuilder';
import { DEFAULT_SCORING_WEIGHTS } from './severityScale';
import { REVIEW_COACH_SEVERITY_THRESHOLD } from '../utils/severity';

// ─── Gate Thresholds ──────────────────────────────────────────────────────────

const GATES: GateThresholds = {
  productionMinScore: 70,
  // raised from 60
  assessmentMinScore: 82,
  // raised from 75
  // SSOT: governance/utils/severity.ts (detangle §10.5). Was inline 45
  // ("lowered from 50 — more questions flagged for review"); now shared so the
  // gate default and the display 'review' bucket can never drift apart.
  reviewCoachSeverityThreshold: REVIEW_COACH_SEVERITY_THRESHOLD,
  assessmentMinQuestionsPerFamily: 3
};

// ─── Default Policy ───────────────────────────────────────────────────────────

export const DEFAULT_POLICY: GovernancePolicy = {
  // C7 (Sprint 2 / audit v1.3): bumped 1.0.0 → 4.0.0 to reflect the pipeline
  // architecture version. Previous bump-policy was implicit; from now on bump
  // major when stage contracts change, minor when checks/thresholds change.
  version: '4.0.0',
  // ── Vocabulary ────────────────────────────────────────────────────────────
  vocab: {
    forbiddenByTierBand: {
      basic: [
      // Original
      'notatie', 'commutatief', 'associatief', 'proportioneel', 'distributief', 'exponentieel',
      // NEW from CONTENT_GOVERNANCE_FRAMEWORK §1.1 — forbidden in groep 6 question text
      'strategie', 'posities', 'algoritme', 'noemer', 'teller', 'decimaalgetal'],
      intermediate: [
      // Original
      'commutatief', 'associatief', 'distributief',
      // NEW from CONTENT_GOVERNANCE_FRAMEWORK §1.1 — still too complex for groep 7
      'algoritme', 'exponentieel'],
      advanced: [],
      expert: []
    }
  },
  // ── Content Budgets ───────────────────────────────────────────────────────
  contentBudgets: {
    maxQuestionLengthByTierBand: {
      basic: 200,
      // Tiers 1-4
      intermediate: 400,
      // Tiers 5-8
      advanced: 600,
      // Tiers 9-12
      expert: 800 // Tiers 13-16
    },
    minExplanationLengthByTierBand: {
      basic: 30,
      intermediate: 50,
      advanced: 80,
      expert: 100
    },
    minDistractorLengthByTierBand: {
      basic: 15,
      intermediate: 25,
      advanced: 35,
      expert: 45
    },
    maxContextLengthByTierBand: {
      basic: 800,
      // Tiers 1-4: short reading passages
      intermediate: 1200,
      // Tiers 5-8
      advanced: 2000,
      // Tiers 9-12
      expert: 3000 // Tiers 13-16: full reading comprehension
    },
    // O1-B / GSR-10 (GOVERNANCE_SYSTEM_HARDENING_AUDIT_2026-06 §4): the former
    // `Taalbegrip` override was a no-op — its maxQuestionLengthByTierBand values
    // (200/400/600/800) were IDENTICAL to the global defaults above, so
    // getMaxQuestionLength() returned the same number whether or not it fired.
    // Removed to eliminate the "two sources for one truth" drift trap. If TAA
    // ever needs a genuinely different budget (e.g. longer reading passages),
    // add a DELIBERATE override here with values that differ from the globals.
    moduleOverrides: {}
  },
  // ── Text Quality (Phase 4) ────────────────────────────────────────────────
  // Maximum sentence length (in words) before a coach-level "long sentence"
  // issue fires. Per tier band so older readers tolerate longer sentences.
  //
  // VALUE-PRESERVING: these values match the historical hardcoded thresholds
  // in `utils/languageQuality.getMaxWordsPerSentence` (18/22/25 with expert
  // mirroring advanced). Changing them is a deliberate didactic decision —
  // discuss in a policy review, don't drift here.
  textQuality: {
    maxSentenceLengthByTierBand: {
      basic: 18,
      // Tiers 1-4: short sentences, lower working-memory load
      intermediate: 22,
      // Tiers 5-8
      advanced: 25,
      // Tiers 9-12: legacy default
      expert: 25 // Tiers 13-16: same as advanced (room to relax later)
    }
  },
  // ── Visual Policy ─────────────────────────────────────────────────────────
  // SINGLE SOURCE OF TRUTH: Auto-generated from seed data via generateGovernanceMapFromSeeds()
  // No manual duplication — the MAP is the authority.
  // LAZY: computed on first access to avoid circular dependency at module init time.
  // See: src/governance/pipeline/visualPolicyBuilder.ts
  visual: {
    requiredFamilies: [],
    familyRules: {}
  },
  // ── Support Policy ────────────────────────────────────────────────────────
  // Migrated from familySupportRegistry.ts requiresVisual/requiresHints flags
  support: {
    familyRules: {
      // SWW — all require hints
      'SWW-TT-STAM': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SWW-TT-INV-JIJ': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SWW-VT-STERK': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SWW-VT-ZWAK-KOF': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SWW-VD-KOF': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SWW-TT-DSTAM': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: false
      },
      'SWW-LEEN-ENG-KOF': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      // SPR — all require hints + visual support
      'SPR-KLANK-JAGER': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SPR-KLANK-BAKKER': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SPR-KLANK-AA-EE-OO': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SPR-KLANK-VERDUBBEL': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'SPR-KLANK-VERKORTEN': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      // GET — mix of hints + visuals
      'GET-GROOT-POS': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'GET-BREUK-GEL-PLUS': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'GET-KOMMA-WAARDE': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      // MET — require visuals
      'MET-LEN-OMREKEN': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      'MET-OPP-RECHT': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      // TIJD — clock families (was TGL, renamed T8 Phase 2)
      'TIJD-ANALOOG': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      },
      // GELD — money families (was TGL, renamed T8 Phase 2)
      'GELD-TERUG': {
        hintsRequired: true,
        explanationRequired: true,
        visualSupportRequired: true
      }
    }
  },
  // ── Question Type Policy ──────────────────────────────────────────────────
  questionType: {
    typeRules: {
      multiple_choice: {
        requiredFields: ['question', 'options', 'correctAnswer'],
        assessmentEligible: true // ✅ ONLY MC is assessment-eligible — this is the SINGLE source of truth
      },
      fill_in: {
        requiredFields: ['question', 'correctAnswer'],
        assessmentEligible: false,
        dataFields: {
          required: [],
          optional: ['acceptedAnswers', 'caseSensitive', 'placeholder', 'inputType']
        }
      },
      ordering: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['items', 'correctOrder'],
          optional: []
        }
      },
      matching: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['leftItems', 'rightItems', 'correctMatches'],
          optional: ['distractors']
        }
      },
      table_reading: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: false,
        // CHANGED: MC-only policy — table_reading is interactive, not pure MC
        dataFields: {
          required: ['headers', 'rows'],
          optional: ['caption', 'highlightCell']
        }
      },
      bar_chart: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: false,
        // CHANGED: MC-only policy — bar_chart requires visual interaction
        dataFields: {
          required: ['labels', 'values'],
          optional: ['title', 'xLabel', 'yLabel']
        }
      },
      line_chart: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: false,
        // CHANGED: MC-only policy — line_chart requires visual interaction
        dataFields: {
          required: ['labels', 'values'],
          optional: ['title', 'xLabel', 'yLabel']
        }
      },
      clock: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        // CHANGED: MC-only policy — clock is interactive
        dataFields: {
          required: ['mode', 'target'],
          optional: ['analog', 'digital', 'options']
        }
      },
      clock_set: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['mode', 'target'],
          optional: ['analog']
        }
      },
      money: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        // CHANGED: MC-only policy — money is interactive
        dataFields: {
          required: ['mode', 'targetCents', 'allowedDenoms'],
          optional: ['priceCents', 'paidCents', 'constraints']
        }
      },
      schedule_reading: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: false,
        // CHANGED: MC-only policy — schedule_reading requires visual interaction
        dataFields: {
          required: ['headers', 'rows'],
          optional: ['title', 'highlightCell']
        }
      },
      ratio_table: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['headers', 'rows', 'hiddenCells'],
          optional: []
        }
      },
      number_sequence: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['sequence', 'missingIndex', 'correctValue'],
          optional: ['rule']
        }
      },
      drag_drop_fill: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['template', 'draggables', 'correctPlacements'],
          optional: ['distractors']
        }
      },
      text_highlight: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['text', 'correctHighlights'],
          optional: ['maxHighlights']
        }
      },
      pie_chart: {
        requiredFields: ['question', 'data', 'options', 'correctAnswer'],
        assessmentEligible: false,
        // CHANGED: MC-only policy — pie_chart requires visual interaction
        dataFields: {
          required: ['labels', 'values', 'correctIndex'],
          optional: ['title', 'colors', 'hideValues']
        }
      },
      // Consolidated: cijferend_optellen/aftrekken/vermenigvuldigen → cijferend_rekenen
      cijferend_rekenen: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['operand1', 'operand2'],
          optional: ['operation', 'acceptedAnswers', 'showSteps', 'currentStep']
        }
      },
      cijferend_delen: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['operand1', 'operand2'],
          optional: ['acceptedAnswers', 'showSteps', 'currentStep']
        }
      },
      multiplication_grid: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['mode'],
          optional: ['baseNumber', 'range', 'hiddenCells', 'rows', 'cols']
        }
      },
      true_false_series: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['statements'],
          optional: []
        }
      },
      estimation_slider: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['min', 'max', 'correctValue', 'tolerancePercent'],
          optional: ['unit', 'showNumberLine']
        }
      },
      categorize: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['categories', 'items'],
          optional: []
        }
      },
      error_spotting: {
        requiredFields: ['question', 'data'],
        assessmentEligible: false,
        dataFields: {
          required: ['text', 'errors'],
          optional: []
        }
      }
    }
  },
  // ── Notation Policy ───────────────────────────────────────────────────────
  notation: {
    latexFractionModules: ['Getalbegrip'],
    commaDecimalModules: ['Getalbegrip', 'Metriek', 'Geld'],
    superscriptUnitModules: ['Metriek'],
    euroCommaModules: ['Geld']
  },
  // ── Distractor Policy ─────────────────────────────────────────────────────
  // FIX s45: minOptions 2→3 (aligned with schemaStage + SEED_REVIEW_CHECKLIST §D2)
  // O1-A (GOVERNANCE_SYSTEM_HARDENING_AUDIT_2026-06 §4): the per-module
  // `modulePatterns` map was removed (rulebook-shadowed dead duplication).
  // The SSOT is each rulebook's `content.distractorMisconceptionPattern`.
  distractor: {
    minOptions: 3,
    maxOptions: 5,
    explanationsRequired: true,
    misconceptionRefRequired: true
  },
  // ── Explanation Policy ────────────────────────────────────────────────────
  // O1-A (GOVERNANCE_SYSTEM_HARDENING_AUDIT_2026-06 §4): the per-module
  // `moduleRuleRefRequired` + `moduleCheckStrategyMinTier` maps were removed
  // (rulebook-shadowed dead duplication). The SSOT is each rulebook's
  // `explanation.requireRuleReference` + `explanation.checkStrategyMinTier`.
  explanation: {
    required: true,
    flagGeneric: true,
    requireDidactic: true
  },
  // ── Scoring (Phase 4) ─────────────────────────────────────────────────────
  // Penalty weights for computeIssuePenalty(). Values preserve the historical
  // formula exactly (blocker=30, coach=severity/4, info=2). Centralised here
  // so tuning is a single-file change, not a 141-literal sweep.
  scoring: DEFAULT_SCORING_WEIGHTS,
  // ── Quality Thresholds (GSR-24, PR-11) ────────────────────────────────────
  // Tier escalation points for pedagogy / quality checks. Previously inline
  // magic numbers in pedagogyStage.ts (HIGH_STAKES_TIER=9) and
  // questionQualityEngine.ts (>= 7, >= 10). Centralising removes drift and
  // lets admin overrides tune escalation per profile.
  qualityThresholds: {
    highStakesTier: 9,
    // eind-tier / plus content — pedagogy issues become blockers
    distractorExplanationTier: 7,
    // tier ≥ 7: every distractor needs an explanation
    distractorLengthCheckTier: 10 // tier ≥ 10: flag suspiciously short distractors
  },
  // ── Gate Thresholds ───────────────────────────────────────────────────────
  gates: GATES,
  // ── Audience Profiles ─────────────────────────────────────────────────────
  audienceProfiles: [{
    id: 'default',
    name: 'Default',
    description: 'Standard governance profile for all learners (v1)'
  }],
  // ── Feature Flags ─────────────────────────────────────────────────────────
  featureFlags: {
    ldgValidation: true,
    strictDataConvention: true,
    // ACTIVE — interactiveConfig is now a blocker
    enforceDistractorExplanations: true,
    enforceLatex: true,
    // ACTIVE — LaTeX required for fraction modules
    enforceUnitNotation: true,
    // ACTIVE — superscript units required for MET
    enforceSupportCoverage: true // ACTIVE — support coverage validated
    // C3 (hardening-audit): the legacy global `enforcePedagogicalContract`
    // flag was removed. Pedagogy strictness is governed per module by the FG4
    // enforcement ramp (governanceConfigStore.enforcement.pedagogical →
    // applyEnforcementModes). High-stakes-tier escalation (Control C) is
    // independent and still active.
  }
};